import { computed, readonly, ref } from 'vue';

import { isPastWeekKey } from '@/utils/calendarBuckets';

export interface AssignmentDragPayload {
  type: 'assignment';
  rowId: string;
  weekKey: string;
  assignmentId: string;
  poNumber: string;
  vendorId: string;
  capacityTypeId: string;
}

export interface GroupDragPayload {
  type: 'group';
  weekKey: string;
  vendorId: string;
  distributionGroupId: string;
  memberCapacityTypeIds: string[];
}

export type DragPayload = AssignmentDragPayload | GroupDragPayload;

export interface DropTargetCandidate {
  rowId: string;
  vendorId: string;
  capacityTypeId: string;
  weekKey: string;
}

export interface DropResolution {
  kind: 'assignment' | 'group';
  rowId: string;
  weekKey: string;
  assignmentId?: string;
  distributionGroupId?: string;
  targetWeekStart: string;
}

// Module-scoped so every calendar cell/handle instance shares one live drag
// state — the same cross-component sharing idea as useAssignmentHover,
// without touching the Pinia store (this is ephemeral UI state).
const activePayload = ref<DragPayload | null>(null);

function isCompatibleTarget(candidate: DropTargetCandidate): boolean {
  const payload = activePayload.value;
  if (!payload) {
    return false;
  }

  // Work already done cannot be planned differently, so a week that has ended is not a place
  // anything can be dropped. Dragging out of one and into the future stays allowed.
  if (isPastWeekKey(candidate.weekKey)) {
    return false;
  }

  if (payload.type === 'assignment') {
    return (
      candidate.vendorId === payload.vendorId &&
      candidate.capacityTypeId === payload.capacityTypeId &&
      candidate.weekKey !== payload.weekKey
    );
  }

  return (
    candidate.vendorId === payload.vendorId &&
    candidate.weekKey !== payload.weekKey &&
    payload.memberCapacityTypeIds.includes(candidate.capacityTypeId)
  );
}

export function useCalendarDragDrop() {
  const isDragging = computed(() => activePayload.value !== null);

  function startDrag(payload: DragPayload): void {
    activePayload.value = payload;
  }

  function cancelDrag(): void {
    activePayload.value = null;
  }

  function completeDrop(candidate: DropTargetCandidate): DropResolution | null {
    const payload = activePayload.value;
    if (!payload || !isCompatibleTarget(candidate)) {
      return null;
    }

    const resolution: DropResolution =
      payload.type === 'assignment'
        ? {
            kind: 'assignment',
            rowId: candidate.rowId,
            weekKey: payload.weekKey,
            assignmentId: payload.assignmentId,
            targetWeekStart: candidate.weekKey,
          }
        : {
            kind: 'group',
            rowId: candidate.rowId,
            weekKey: payload.weekKey,
            distributionGroupId: payload.distributionGroupId,
            targetWeekStart: candidate.weekKey,
          };

    cancelDrag();

    return resolution;
  }

  return {
    activePayload: readonly(activePayload),
    isDragging,
    startDrag,
    cancelDrag,
    isCompatibleTarget,
    completeDrop,
  };
}
